import type { Block, BlockType } from '@emailforge/schemas';
import { nanoid } from 'nanoid';

export const BLOCK_LIBRARY: Array<{ type: BlockType; label: string; description: string; category: string }> = [
  { type: 'hero', label: 'Hero', description: 'Large banner with title + subtitle', category: 'Content' },
  { type: 'heading', label: 'Heading', description: 'Section title', category: 'Content' },
  { type: 'text', label: 'Text', description: 'Paragraph of body text', category: 'Content' },
  { type: 'button', label: 'Button', description: 'Call-to-action button', category: 'Content' },
  { type: 'image', label: 'Image', description: 'Inline image', category: 'Content' },
  { type: 'divider', label: 'Divider', description: 'Horizontal rule', category: 'Layout' },
  { type: 'spacer', label: 'Spacer', description: 'Vertical whitespace', category: 'Layout' },
  { type: 'columns', label: 'Columns', description: 'Two-column row', category: 'Layout' },
  { type: 'product', label: 'Product', description: 'Product card for commerce', category: 'Commerce' },
  { type: 'footer', label: 'Footer', description: 'Legal + unsubscribe', category: 'Layout' },
  { type: 'social', label: 'Social', description: 'Social media icons', category: 'Layout' },
];

export function createBlock(type: BlockType): Block {
  const id = nanoid(10);
  switch (type) {
    case 'hero':
      return {
        id,
        type: 'hero',
        props: { title: 'Your hero headline', subtitle: 'Supporting copy here.', alignment: 'center', paddingTop: 40, paddingBottom: 40 },
      };
    case 'heading':
      return {
        id,
        type: 'heading',
        props: { text: 'Section heading', level: 'h2', alignment: 'left', paddingTop: 24, paddingBottom: 8 },
      };
    case 'text':
      return {
        id,
        type: 'text',
        props: { text: 'Write your paragraph here. {{handlebars}} works.', alignment: 'left', size: 'md', paddingTop: 8, paddingBottom: 8 },
      };
    case 'button':
      return {
        id,
        type: 'button',
        props: { label: 'Call to action', href: 'https://example.com', variant: 'primary', alignment: 'center', fullWidth: false, paddingTop: 16, paddingBottom: 16 },
      };
    case 'image':
      return {
        id,
        type: 'image',
        props: { src: 'https://via.placeholder.com/600x240?text=image', alt: '', alignment: 'center', paddingTop: 12, paddingBottom: 12 },
      };
    case 'divider':
      return {
        id,
        type: 'divider',
        props: { thickness: 1, paddingTop: 16, paddingBottom: 16 },
      };
    case 'spacer':
      return { id, type: 'spacer', props: { height: 24 } };
    case 'product':
      return {
        id,
        type: 'product',
        props: {
          name: 'Product name',
          price: '€24.00',
          imageUrl: 'https://via.placeholder.com/160',
          href: 'https://example.com/products/foo',
          paddingTop: 12,
          paddingBottom: 12,
        },
      };
    case 'footer':
      return {
        id,
        type: 'footer',
        props: {
          companyName: 'Acme Inc',
          address: 'Calle Falsa 123, Madrid',
          unsubscribeUrl: 'https://example.com/unsubscribe',
          paddingTop: 24,
          paddingBottom: 24,
        },
      };
    case 'social':
      return {
        id,
        type: 'social',
        props: {
          icons: [
            { network: 'twitter', url: 'https://twitter.com/acme' },
            { network: 'linkedin', url: 'https://linkedin.com/company/acme' },
          ],
          alignment: 'center',
          paddingTop: 16,
          paddingBottom: 16,
        },
      };
    case 'columns':
      return {
        id,
        type: 'columns',
        props: { ratio: '1:1', gap: 16 },
        children: [[], []],
      };
  }
}
